/**
 * Rollout flags for the consumer feed filters (Stage C of the
 * consumer-feed-filters spec — `galactus-dev-workspace/docs/agents/handoffs/
 * consumer-feed-filters/handoff.md` @ `Danielle-design/consumer-feed-filters`).
 *
 * Everything the filter path does is behind ONE env flag, default OFF, so the
 * merged code is inert until Dan flips it per lane: g1 `origin main`
 * auto-deploys user-visible staging, and the legacy path must stay
 * bit-for-bit today's behaviour (bare `{op:'subscribe', channel:'markets'}` +
 * explicit snapshot, no fencing, no acks) until then.
 *
 *   VITE_FEED_FILTERS          `on` | `1` | `true` ⇒ send the versioned
 *                              `filter` object on every markets subscribe
 *                              (class/type narrowing + browser cadence),
 *                              install the ack'd fencing pair, drop stale
 *                              frames, and clear out-of-scope rows on every
 *                              filterReset. Anything else ⇒ legacy.
 *   VITE_FEED_FILTERS_FIREHOSE `on` ⇒ ALSO send the filter on the FIREHOSE
 *                              connection. Off by default because the
 *                              firehose is OG hydra on every lane today, and
 *                              OG does not speak Stage A: its subscribe schema
 *                              strips unknown keys (hydra ConsumerProtocol.ts,
 *                              zod default), so it would silently take a bare
 *                              subscribe, send no ack and no stamps — and the
 *                              fence would admit NOTHING. The firehose stays
 *                              legacy until it is a core (or a bench harness).
 *   VITE_FEED_FILTERS_CORES    the filter on CORE fleet feeds (hc1…) — the
 *                              WARM-STANDBY TIER: every sport, class trims,
 *                              cadence 1000 while an edge owns what is on
 *                              screen. ON by default with the flag (Dan,
 *                              2026-09-02: hc1 runs Stage B and carries every
 *                              sport). `off` keeps cores legacy — do that on
 *                              any lane whose core predates Stage B (it
 *                              would strip the object, never ack, and its
 *                              sports would go dark).
 *   VITE_FEED_FILTERS_SPORTS   Phase 2: the `sports` allow-list from the
 *                              mounted views' declarations (the dashboard
 *                              narrows to its selected sport; every other
 *                              route asks for all). ON by default whenever
 *                              VITE_FEED_FILTERS is on — Dan, 2026-09-02, on
 *                              the g1.pm smoke ("I'm on NFL tab, look at that
 *                              Live Feed": wall-to-wall tennis from he1),
 *                              reversing the 2026-06-12 toggle-latency ruling:
 *                              "filtering for only what's visible on a page is
 *                              a good enough first start". `off` disables the
 *                              axis (class narrowing stays). The 30 s narrowing
 *                              grace keeps recently viewed sports warm, so a
 *                              quick chip flip back is still a local re-filter.
 *
 * RUNTIME OVERRIDE (per browser, no rebuild — the staging smoke + Stage D
 * A/B on ONE deploy): every flag can be forced `on`/`off` from the URL once
 * (`?feedFilters=on`, `?feedFiltersCores=on`, `?feedFiltersFirehose=on`,
 * `?feedFiltersSports=on`), which persists to localStorage
 * (`g1xl.feedFilters` …) and applies from the next page load; `=env` clears
 * the override. The override wins over the env in both directions. Read at
 * fleet construction (page load) — the SharedWorker latches the mode per
 * worker name, so a change needs a reload (and, for a shared worker, the
 * other tabs of that feed reload too).
 *
 * Read at call time (not module load) so unit tests can `vi.stubEnv` both.
 */

function envOn(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === 'on' || v === '1' || v === 'true';
}
function envOff(raw: string | undefined): boolean {
  const v = raw?.trim().toLowerCase();
  return v === 'off' || v === '0' || v === 'false';
}

const OVERRIDE_KEYS = {
  filters: 'g1xl.feedFilters',
  cores: 'g1xl.feedFiltersCores',
  firehose: 'g1xl.feedFiltersFirehose',
  sports: 'g1xl.feedFiltersSports',
  games: 'g1xl.feedFiltersGames',
} as const;
const QUERY_KEYS: Record<keyof typeof OVERRIDE_KEYS, string> = {
  filters: 'feedFilters', cores: 'feedFiltersCores', firehose: 'feedFiltersFirehose', sports: 'feedFiltersSports',
  games: 'feedFiltersGames',
};

/** Fold `?feedFilters…=on|off|env` URL params into localStorage. Idempotent;
 *  safe without window/storage (tests, workers). Returns true if any key was
 *  written, so a caller can strip the params if it wants to. */
export function applyFeedFilterUrlOverrides(search?: string): boolean {
  if (typeof window === 'undefined') return false;
  let wrote = false;
  try {
    const params = new URLSearchParams(search ?? window.location.search);
    for (const k of Object.keys(OVERRIDE_KEYS) as Array<keyof typeof OVERRIDE_KEYS>) {
      const v = params.get(QUERY_KEYS[k])?.trim().toLowerCase();
      if (v == null) continue;
      if (v === 'env' || v === '') localStorage.removeItem(OVERRIDE_KEYS[k]);
      else localStorage.setItem(OVERRIDE_KEYS[k], envOn(v) ? 'on' : 'off');
      wrote = true;
    }
  } catch { /* storage unavailable — env decides */ }
  return wrote;
}

/** null = no override (env decides); true/false = forced. */
function override(k: keyof typeof OVERRIDE_KEYS): boolean | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const v = localStorage.getItem(OVERRIDE_KEYS[k]);
    return v == null ? null : v === 'on';
  } catch {
    return null;
  }
}

function flag(k: keyof typeof OVERRIDE_KEYS, envRaw: string | undefined): boolean {
  return override(k) ?? envOn(envRaw);
}
/** Default-ON variant: only an explicit `off` (override or env) disables. */
function flagDefaultOn(k: keyof typeof OVERRIDE_KEYS, envRaw: string | undefined): boolean {
  const o = override(k);
  return o != null ? o : !envOff(envRaw);
}

/** Phase 1: class/type narrowing + cadence + fencing (edges). */
export function feedFiltersEnabled(): boolean {
  return flag('filters', import.meta.env.VITE_FEED_FILTERS as string | undefined);
}

/** Phase 2: per-sport narrowing. Requires Phase 1; ON unless explicitly off. */
export function feedFilterSportsEnabled(): boolean {
  return feedFiltersEnabled() && flagDefaultOn('sports', import.meta.env.VITE_FEED_FILTERS_SPORTS as string | undefined);
}

/** Phase 3: per-EVENT narrowing (the games axis — hydracore e37e33d,
 *  hydraedge c93b18e). Requires Phase 2, because a game is a narrowing WITHIN
 *  a sport and the surfaces that declare a game declare its sport too.
 *
 *  ON with the flag, like the sports axis (hydra 2026-09-04 closed the
 *  declaration with option 1). The keys a page declares come from
 *  `marketStore.getGameWireKeys` — the wire values its OWN rows carry — so a
 *  key the engines cannot match is unconstructible here. Before a page's rows
 *  land it has no keys and declares its sport alone, so the axis simply does
 *  not ride yet; it never rides empty. Per-browser kill switch:
 *  `?feedFiltersGames=off`. */
export function feedFilterGamesEnabled(): boolean {
  return feedFilterSportsEnabled() && flagDefaultOn('games', import.meta.env.VITE_FEED_FILTERS_GAMES as string | undefined);
}

/** The firehose (OG today) speaks the filter only when explicitly told so. */
export function feedFilterFirehoseEnabled(): boolean {
  return feedFiltersEnabled() && flag('firehose', import.meta.env.VITE_FEED_FILTERS_FIREHOSE as string | undefined);
}

/** Cores speak the filter (the warm-standby tier) unless explicitly off. */
export function feedFilterCoresEnabled(): boolean {
  return feedFiltersEnabled() && flagDefaultOn('cores', import.meta.env.VITE_FEED_FILTERS_CORES as string | undefined);
}

/** Resolved once per client construction and shipped to the SharedWorker on
 *  `__init` (the worker is transport-agnostic and never reads env). */
export interface FeedFilterMode {
  enabled: boolean;
  sportsAxis: boolean;
  /** Games axis (one event). Edges only: the core floor is the background for
   *  every game, so narrowing it to one event would strand every held
   *  position's mark. */
  gamesAxis?: boolean;
  /** Requested markets cadence for this connection. Browsers 250; the core
   *  floor 1000 while it is the warm standby (re-negotiated at runtime by
   *  the fleet — see useHydra floorCadence). */
  cadenceMs?: number;
}

export function resolveFeedFilterMode(): FeedFilterMode {
  return { enabled: feedFiltersEnabled(), sportsAxis: feedFilterSportsEnabled(), gamesAxis: feedFilterGamesEnabled() };
}

/** Per-feed mode: edges follow VITE_FEED_FILTERS; cores additionally need
 *  VITE_FEED_FILTERS_CORES and the firehose VITE_FEED_FILTERS_FIREHOSE (see
 *  above) — one switch per engine family, matching how the families deploy.
 *  A dedicated OP transport never sends a markets filter (it subscribes no
 *  markets at all — HydraClient short-circuits on opOnly regardless). */
export function feedFilterModeFor(role: 'firehose' | 'core' | 'edge'): FeedFilterMode {
  const enabled = role === 'firehose' ? feedFilterFirehoseEnabled()
    : role === 'core' ? feedFilterCoresEnabled()
    : feedFiltersEnabled();
  // The two-tier model: edges narrow to what is on screen; the core floor
  // never narrows by sport (it is the background for everything) and runs
  // at the standby cadence until the fleet says otherwise.
  if (role === 'core') return { enabled, sportsAxis: false, gamesAxis: false, cadenceMs: 1000 };
  return {
    enabled,
    sportsAxis: enabled && feedFilterSportsEnabled(),
    // EDGES ONLY (Danielle R16 DNL-014): the firehose was inheriting this from
    // the shared branch, contrary to the contract the doc comment states.
    //
    // The reason is the one stated at the top of this file, NOT a fallback
    // argument: OG does not speak Stage A. Its subscribe schema strips unknown
    // keys, so a `games` allow-list sent there is silently discarded and the
    // connection keeps delivering everything while this client believes it
    // narrowed — the worst shape of wrong, since nothing surfaces the gap.
    // (Dan, 2026-09-04, correcting an earlier draft of this comment: OG is NOT
    // a fallback surface. It carries essentially only Unabated now and is being
    // retired; hydracore is the WebSocket fallback and the REST primary. The
    // connection that must never narrow is therefore the CORE floor, which is
    // carved out above.) Only an edge, which speaks the grammar and whose sport
    // is already on screen, may carry this axis.
    gamesAxis: role === 'edge' && enabled && feedFilterGamesEnabled(),
    cadenceMs: 250,
  };
}
